<template>
	<li
		v-if="mortgagePart"
		class="grid grid-cols-subgrid col-span-full w-full items-stretch"
	>
		<div
			class="grid grid-cols-subgrid col-span-full [&>*]:border-b [&>*]:not-first:border-s h-11 [&_.app-field]:h-full [&_input,&_select]:border-none [&_.app-field]:focus-within:z-10 [&_.app-field:has(input,select):hover]:z-10 [&_input,&_select]:rounded-none [&_input,&_select]:-outline-offset-1 [&_input,&_select]:hover:not-focus-visible:outline-1 [&_input,&_select]:hover:not-focus-visible:outline-ring"
		>
			<div
				class="self-stretch place-self-stretch flex items-center justify-center"
			>
				<AppIcon
					v-if="mortgagePart.existingMortgagePartId"
					name="link"
					class="text-xl text-[var(--text-secondary)]"
				/>
			</div>
			<AppInput
				v-model="mortgagePart.amount"
				:value-type="ValueType.Currency"
				class="items-stretch"
			/>
			<AppInput
				v-model="mortgagePart.repaymentForm"
				type="dropdown"
				class="grow"
				:options="repaymentFormOptions"
			/>
			<AppInput
				:model-value="durationMonthsDisplay"
				:value-type="ValueType.Number"
				:read-only="hasFixedEndDate"
				@update:model-value="
					(val) =>
						!hasFixedEndDate &&
						mortgagePart &&
						(mortgagePart.durationMonths = val)
				"
				@click="handleDurationClick"
			/>
			<AppInput
				:model-value="fixedInterestMonthsDisplay"
				:value-type="ValueType.Number"
				:read-only="hasFixedEndDate"
				@update:model-value="
					(val) =>
						!hasFixedEndDate &&
						mortgagePart &&
						(mortgagePart.fixedInterestMonths = val)
				"
				@click="handleFixedInterestClick"
			/>
			<AppInput
				v-model="mortgagePart.interestRate"
				:value-type="ValueType.Percentage"
			/>

			<div class="sticky right-0 bg-white z-20 border-l">
				<AppButton
					variant="ghost"
					:icon="collapsed ? 'edit_arrow_down' : 'edit_arrow_up'"
					class="w-full h-full aspect-auto rounded-none"
					:aria-label="collapsed ? 'Uitklappen' : 'Inklappen'"
					:aria-expanded="!collapsed"
					@click="collapsed = !collapsed"
				/>
			</div>
		</div>

		<div
			v-if="!collapsed"
			class="col-span-full justify-between border-b bg-gray-50 flex items-start p-2 sticky left-0 pb-6"
			:style="{ width: cardWidth }"
		>
			<div class="flex flex-col gap-2 text-sm">
				<AppInput
					v-model="mortgagePart.existingMortgagePartId"
					label="Oorsprong"
					type="dropdown"
					:options="[
						{ value: undefined, label: 'Nieuw leningdeel' },
						...availableMortgageParts.map((mp) => ({
							label: existingMortgagePartLabel(mp),
							value: mp.id,
						})),
					]"
				/>

				<AppAutocomplete
					:key="data.mortgage.lenderCode"
					v-model="mortgagePart.productCode"
					:disabled="!data.mortgage.lenderCode"
					label="Productcode"
					empty-message="Geen producten gevonden"
					:open-on-focus="!mortgagePart?.productCode"
					:has-icon="false"
					:options="partProductOptions"
					:option-loader="getPartProductOption"
				/>

				<AppInput
					v-model="hasFixedEndDate"
					label="Vaste einddatum"
					type="option"
					:options="[
						{ value: true, label: 'Ja' },
						{ value: false, label: 'Nee' },
					]"
				/>

				<div class="flex gap-2">
					<AppInput
						v-if="hasFixedEndDate"
						ref="endDateInput"
						v-model="mortgagePart.endDate"
						label="Einddatum"
						class="w-full max-w-[200px]"
						:value-type="ValueType.Date"
						:show-display-date="false"
					/>

					<AppInput
						v-if="hasFixedEndDate"
						ref="fixedInterestEndDateInput"
						v-model="mortgagePart.fixedInterestEndDate"
						label="Einddatum rentevastperiode"
						class="w-full max-w-[200px]"
						:value-type="ValueType.Date"
						:show-display-date="false"
					/>
				</div>

				<div class="flex gap-2">
					<AppInput
						v-model="mortgagePart.interestDeductionLaw"
						label="Recht"
						type="option"
						:options="[
							{ value: 'none', label: 'Geen' },
							{ label: 'Oud', value: 'old' },
							{ label: 'Nieuw', value: 'new' },
						]"
					/>

					<AppInput
						v-if="
							mortgagePart.interestDeductionLaw === 'old' ||
							mortgagePart.interestDeductionLaw === 'new'
						"
						v-model="mortgagePart.interestDeductionEndDate"
						label="Einddatum renteaftrek"
						class="w-full max-w-[200px]"
						:value-type="ValueType.Date"
						:show-display-date="false"
					/>
				</div>

				<div class="flex gap-2">
					<AppInput
						v-model="mortgagePart.interestReflectionPeriod"
						label="Rentebedenktijd"
						type="dropdown"
						:options="[
							{ value: undefined, label: 'Niet opgeven' },
							{ value: 'none', label: 'Geen' },
							{ value: 'upfront', label: 'Vooraf' },
							{ value: 'afterwards', label: 'Achteraf' },
						]"
					/>

					<AppInput
						v-if="
							mortgagePart.interestReflectionPeriod === 'upfront' ||
							mortgagePart.interestReflectionPeriod === 'afterwards'
						"
						v-model="mortgagePart.interestReflectionPeriodMonths"
						label="Bedenktijd (maanden)"
						:value-type="ValueType.Number"
					/>
				</div>

				<div class="flex gap-2">
					<AppInput
						v-model="mortgagePart.interestRateType"
						label="Rentevorm"
						type="dropdown"
						:options="[
							{ value: undefined, label: 'Niet opgeven' },
							{ value: 'fixed', label: 'Rentevast' },
							{ value: 'dampened', label: 'Rentedempend' },
							{ value: 'variable', label: 'Variabel' },
						]"
					/>
				</div>

				<div
					v-if="mortgagePart.repaymentForm === 'interest-only'"
					class="flex gap-2"
				>
					<AppInput
						v-model="mortgagePart.repaymentStrategy"
						label="Wijze van terugbetaling"
						type="dropdown"
						:options="repaymentStrategyOptions"
					/>
				</div>
			</div>

			<AppButton variant="danger" icon="delete" @click="emit('delete')">
				Verwijderen
			</AppButton>
		</div>
	</li>
</template>

<script setup lang="ts">
import type {
	ExistingMortgagePart,
	MortgagePart,
} from "@loanlink-nl/model-app";
import { ValueType } from "@/enums/ValueType";
import { ref, watch, computed, nextTick, useTemplateRef } from "vue";
import { asDisplayCur, getInterestLabel } from "@/helpers/inputHelpers";
import { repaymentFormOptions } from "../existing-mortgage/repayment-form";
import { useCalculationDate } from "@/composables/useCalculationDate";
import { getMonthSpan } from "@loanlink-nl/shared";
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { useQuery } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { storeToRefs } from "pinia";

const emit = defineEmits<{
	(e: "delete"): void;
}>();
const mortgagePart = defineModel<MortgagePart>();

defineProps<{
	availableMortgageParts: ExistingMortgagePart[];
	cardWidth?: string;
}>();

const { passingDateString } = useCalculationDate();
const dossierDataStore = useDossierDataStore();
const { data } = storeToRefs(dossierDataStore);
const { data: partProducts } = useQuery(() =>
	queries.partProducts.forProvider({
		providerCode: data.value.mortgage.lenderCode ?? "",
	}),
);
const partProductOptions = computed(() =>
	(partProducts.value ?? [])
		.map((p) => ({
			value: p.productPartCode,
			label: splitHdnValue(p.productPartCode)[1],
		}))
		.toSorted((a, b) => a.label.localeCompare(b.label)),
);
function getPartProductOption(code: string) {
	return partProductOptions.value.find((o) => o.value === code);
}

const repaymentStrategyOptions = [
	{ value: undefined, label: "Niet opgeven" },
	{ value: "own-funds", label: "Aflossen uit eigen middelen" },
	{ value: "property-sale", label: "Aflossen uit verkoop onderpand" },
	{ value: "refinance", label: "Blijven lenen / opnieuw lenen" },
];
const collapsed = ref(true);
const hasFixedEndDate = ref(false);
const endDateInput = useTemplateRef("endDateInput");
const fixedInterestEndDateInput = useTemplateRef("fixedInterestEndDateInput");

function openAndFocus(el: () => HTMLElement) {
	if (hasFixedEndDate.value) {
		if (collapsed.value) {
			collapsed.value = false;
		}

		void nextTick(() => {
			const input = el()?.querySelector("input");
			if (input) {
				input.focus();
			}
		});
	}
}

function handleDurationClick() {
	openAndFocus(() => (endDateInput.value as { $el: HTMLElement })?.$el);
}

function handleFixedInterestClick() {
	openAndFocus(
		() => (fixedInterestEndDateInput.value as { $el: HTMLElement })?.$el,
	);
}

const durationMonthsDisplay = computed(() => {
	if (mortgagePart.value?.durationMonths) {
		return mortgagePart.value.durationMonths;
	}
	return getMonthSpan(passingDateString.value, mortgagePart.value?.endDate);
});

const fixedInterestMonthsDisplay = computed(() => {
	if (mortgagePart.value?.fixedInterestMonths) {
		return mortgagePart.value.fixedInterestMonths;
	}
	return getMonthSpan(
		passingDateString.value,
		mortgagePart.value?.fixedInterestEndDate,
	);
});

// Set hasFixedEndDate to 'Ja' if any date fields are present
watch(
	() => ({
		endDate: mortgagePart.value?.endDate,
		fixedInterestEndDate: mortgagePart.value?.fixedInterestEndDate,
	}),
	(dates) => {
		if (dates.endDate || dates.fixedInterestEndDate) {
			hasFixedEndDate.value = true;
		}
	},
	{ immediate: true },
);

// Clear date fields when hasFixedEndDate is set to 'Nee'
// and set the calculated values on the mortgage part
watch(hasFixedEndDate, (value) => {
	if (!value && mortgagePart.value) {
		// Set calculated values before clearing dates
		const calculatedDuration = getMonthSpan(
			passingDateString.value,
			mortgagePart.value.endDate,
		);
		const calculatedFixedInterest = getMonthSpan(
			passingDateString.value,
			mortgagePart.value.fixedInterestEndDate,
		);

		if (calculatedDuration !== undefined) {
			mortgagePart.value.durationMonths = calculatedDuration;
		}
		if (calculatedFixedInterest !== undefined) {
			mortgagePart.value.fixedInterestMonths = calculatedFixedInterest;
		}

		// Clear date fields
		mortgagePart.value.endDate = undefined;
		mortgagePart.value.fixedInterestEndDate = undefined;
	}
});

watch(
	() => mortgagePart.value?.interestDeductionLaw,
	(recht) => {
		if (recht !== "old" && recht !== "new" && mortgagePart.value) {
			mortgagePart.value.interestDeductionEndDate = undefined;
		}
	},
	{ immediate: true },
);

watch(
	() => mortgagePart.value?.interestReflectionPeriod,
	(period) => {
		if (period !== "upfront" && period !== "afterwards" && mortgagePart.value) {
			mortgagePart.value.interestReflectionPeriodMonths = undefined;
		}
	},
	{ immediate: true },
);

watch(
	() => mortgagePart.value?.repaymentForm,
	(form) => {
		if (form !== "interest-only" && mortgagePart.value) {
			mortgagePart.value.repaymentStrategy = undefined;
		}
	},
	{ immediate: true },
);

function existingMortgagePartLabel(part: ExistingMortgagePart) {
	const parts = ["Leningdeel"];

	const repaymentLabel = part.repaymentForm
		? repaymentFormOptions.find((opt) => opt.value === part.repaymentForm)
				?.label
		: undefined;
	if (repaymentLabel) {
		parts.push(repaymentLabel);
	}

	if (part.amountCurrent) {
		parts.push(asDisplayCur(part.amountCurrent));
	}

	if (part.interestRate) {
		parts.push(getInterestLabel(part.interestRate));
	}

	if (part.lenderMortgagePartId) {
		parts.push(part.lenderMortgagePartId);
	}

	return parts.join(" - ");
}
</script>
